import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import DashboardWrapper from "../containers/DashboardWrapper";
import { setCurrentPage } from "../actions";
import LocalStorageHelper from "../utils/helper/localstorage_helper";
import Period from "../components/Filter/Period";
import PlotMapboxMap from "../components/Plot/PlotMapboxMap";
import moment from "moment-timezone";

const DEFAULT_MAP_CENTER = [139.767125, 35.681236];
const sidePanelWidth = 360;

const plotItems = [
  {
    id: "PLT-00041",
    name: "品川区 井戸",
    event: "防災チャンピオンシップ",
    municipality: "品川区",
    date: "2026-07-01",
    lv: 4,
    assetType: "井戸",
    lng: 139.7202,
    lat: 35.6120,
    photos: [
      { id: "plot-00041-a", label: "撮影者A", thumb: "#b7d8b3" },
      { id: "plot-00041-b", label: "撮影者B", thumb: "#9dc2a1" },
      { id: "plot-00041-c", label: "撮影者C", thumb: "#d0c0a8" },
    ],
  },
  {
    id: "PLT-00063",
    name: "渋谷区 電柱",
    event: "防災チャンピオンシップ",
    municipality: "渋谷区",
    date: "2026-07-02",
    lv: 3,
    assetType: "電柱",
    lng: 139.7016,
    lat: 35.6598,
    photos: [
      { id: "plot-00063-a", label: "撮影者D", thumb: "#b5bfe5" },
      { id: "plot-00063-b", label: "撮影者E", thumb: "#c4cbe8" },
    ],
  },
  {
    id: "PLT-00110",
    name: "品川区 防火水槽",
    event: "防災チャンピオンシップ",
    municipality: "品川区",
    date: "2026-07-03",
    lv: 2,
    assetType: "防火水槽",
    lng: 139.7180,
    lat: 35.6075,
    photos: [{ id: "plot-00110-a", label: "撮影者F", thumb: "#b7e0bf" }],
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "calc(100vh - 64px)",
    display: "flex",
    flexDirection: "column",
    background:
      "radial-gradient(circle at top left, rgba(41, 128, 185, 0.12), transparent 30%), linear-gradient(180deg, #f7fafc 0%, #eef4f8 100%)",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
    background: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(10px)",
  },
  topBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "nowrap",
    minWidth: 0,
  },
  topBarRight: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginLeft: "auto",
    minWidth: 0,
  },
  titleBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: "#0f172a",
  },
  subtitle: {
    fontSize: 12,
    color: "#64748b",
  },
  countText: {
    fontSize: 12,
    color: "#10b981",
    border: "1px solid #10b981",
    borderRadius: 999,
    padding: "4px 10px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  filter: {
    minWidth: 150,
    background: "#fff",
    flexShrink: 0,
  },
  clearButton: {
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  content: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
  },
  mapArea: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    position: "relative",
  },
  mapShell: {
    flex: 1,
    position: "relative",
    margin: theme.spacing(2),
    borderRadius: 24,
    overflow: "hidden",
    background:
      "linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88))",
    boxShadow: "0 18px 48px rgba(15, 23, 42, 0.16)",
  },
  mapGrid: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
    backgroundSize: "64px 64px",
    opacity: 0.55,
  },
  legend: {
    position: "absolute",
    top: 16,
    right: 16,
    zIndex: 2,
    background: "rgba(255,255,255,0.94)",
    borderRadius: 16,
    padding: theme.spacing(1.5),
    minWidth: 180,
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
  },
  legendTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: "#64748b",
    marginBottom: theme.spacing(1),
  },
  legendRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: "#334155",
    marginBottom: 6,
  },
  dot: {
    width: 11,
    height: 11,
    borderRadius: "50%",
    flexShrink: 0,
  },
  pin: {
    position: "absolute",
    transform: "translate(-50%, -100%)",
    zIndex: 1,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
  },
  pinBubble: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontSize: 18,
    fontWeight: 700,
    boxShadow: "0 8px 18px rgba(0,0,0,0.28)",
    border: "3px solid rgba(255,255,255,0.95)",
  },
  pinStem: {
    width: 0,
    height: 0,
    borderLeft: "7px solid transparent",
    borderRight: "7px solid transparent",
    borderTop: "10px solid rgba(255,255,255,0.95)",
    marginTop: -4,
  },
  thumbWrap: {
    padding: theme.spacing(2, 2, 2),
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.25),
  },
  thumbCard: {
    border: "1px solid rgba(148, 163, 184, 0.28)",
    borderRadius: 14,
    overflow: "hidden",
    background: "#fff",
    cursor: "pointer",
    transition: "transform 120ms ease, box-shadow 120ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 10px 18px rgba(15, 23, 42, 0.12)",
    },
  },
  thumbPreview: {
    height: 92,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    color: "#fff",
  },
  thumbMeta: {
    padding: theme.spacing(1),
    fontSize: 12,
    color: "#0f172a",
  },
  thumbSub: {
    fontSize: 11,
    color: "#64748b",
    marginTop: 2,
  },
  footerNote: {
    position: "absolute",
    left: 16,
    bottom: 16,
    zIndex: 2,
    background: "rgba(15, 23, 42, 0.66)",
    color: "#fff",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 12,
  },
  sidePanel: {
    width: 0,
    flexShrink: 0,
    overflow: "hidden",
    borderLeft: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#fff",
    transition: "width 180ms ease",
  },
  sidePanelOpen: {
    width: sidePanelWidth,
  },
  sidePanelInner: {
    width: sidePanelWidth,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  sidePanelHeader: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  sidePanelTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#0f172a",
  },
  sidePanelSub: {
    fontSize: 12,
    color: "#64748b",
    marginTop: 4,
  },
  sidePanelClose: {
    border: "none",
    background: "transparent",
    padding: 0,
    lineHeight: 0,
    color: "#9ca3af",
    cursor: "pointer",
    flexShrink: 0,
    marginTop: -2,
    "&:hover": {
      color: "#6b7280",
    },
  },
  sidePanelTitleWrap: {
    minWidth: 0,
    flex: 1,
  },
}));

const Plot = ({ setCurrentPage, dateRange }) => {
  const classes = useStyles();
  const [selectedEvent, setSelectedEvent] = useState("all");
  const [selectedMuni, setSelectedMuni] = useState("all");
  const [activePlot, setActivePlot] = useState(null);
  const companyName = LocalStorageHelper.getCompany();
  const currentAccessKey = localStorage.getItem("access_key");
  const providerLabel = currentAccessKey === "dea" ? "DEA" : companyName.toUpperCase();

  React.useEffect(() => {
    setCurrentPage(window.location.pathname);
  }, [setCurrentPage]);

  const eventOptions = useMemo(
    () => ["all", ...new Set(plotItems.map((item) => item.event))],
    []
  );
  const muniOptions = useMemo(
    () => ["all", ...new Set(plotItems.map((item) => item.municipality))],
    []
  );

  const filteredPlots = plotItems.filter((item) => {
    if (selectedEvent !== "all" && item.event !== selectedEvent) return false;
    if (selectedMuni !== "all" && item.municipality !== selectedMuni) return false;
    if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
      const start = moment(dateRange[0]).format("YYYY-MM-DD");
      const end = moment(dateRange[1]).format("YYYY-MM-DD");
      if (item.date < start || item.date > end) return false;
    }
    return true;
  });

  React.useEffect(() => {
    if (activePlot && !filteredPlots.some((plot) => plot.id === activePlot.id)) {
      setActivePlot(null);
    }
  }, [activePlot, filteredPlots]);

  const openViewer = (photoId) => {
    window.open(`/viewer/${encodeURIComponent(photoId)}`, "_blank", "noopener,noreferrer");
  };
  const isPanelOpen = Boolean(activePlot);

  return (
    <DashboardWrapper padding={false}>
      <div className={classes.root}>
        <div className={classes.topBar}>
          <div className={classes.topBarLeft}>
            <div className={classes.titleBlock}>
              <div className={classes.title}>プロット</div>
            </div>
            <FormControl variant="outlined" size="small" className={classes.filter}>
              <InputLabel>イベント</InputLabel>
              <Select
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
                label="イベント"
              >
                {eventOptions.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value === "all" ? "全て" : value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" className={classes.filter}>
              <InputLabel>自治体</InputLabel>
              <Select
                value={selectedMuni}
                onChange={(e) => setSelectedMuni(e.target.value)}
                label="自治体"
              >
                {muniOptions.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value === "all" ? "全て" : value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div className={classes.countText}>プロット数: {filteredPlots.length}</div>
            {(selectedEvent !== "all" || selectedMuni !== "all") && (
              <Button className={classes.clearButton} onClick={() => {
                setSelectedEvent("all");
                setSelectedMuni("all");
              }}>
                クリア
              </Button>
            )}
          </div>
          <div className={classes.topBarRight}>
            <Period
              companyName={companyName}
              plotMode={true}
              onApply={() => {}}
            />
          </div>
        </div>

        <div className={classes.content}>
          <div className={classes.mapArea}>
            <div className={classes.mapShell}>
              <PlotMapboxMap
                plots={filteredPlots}
                activePlotId={activePlot?.id}
                onSelectPlot={setActivePlot}
                panelOpen={isPanelOpen}
                initialCenter={DEFAULT_MAP_CENTER}
                initialZoom={11.7}
              />
              <div className={classes.legend}>
                <div className={classes.legendTitle}>信頼度</div>
                {[4, 3, 2, 1].map((lv) => (
                  <div className={classes.legendRow} key={lv}>
                    <span className={classes.dot} style={{ background: {
                      4: "#22c55e",
                      3: "#eab308",
                      2: "#fb923c",
                      1: "#ef4444",
                    }[lv] }} />
                    <span>Lv{lv}</span>
                  </div>
                ))}
                <Divider style={{ margin: "8px 0" }} />
                <div className={classes.legendTitle}>アセット種別</div>
                {["井戸", "電柱", "消火栓"].map((type) => (
                  <div className={classes.legendRow} key={type}>
                    <span className={classes.dot} style={{ background: "#94a3b8" }} />
                    <span>{type}</span>
                  </div>
                ))}
              </div>
              {/* <div className={classes.footerNote}>
                マーカーをクリックすると、撮影者別サムネイルを表示します。
              </div> */}
            </div>
          </div>

          <div
            className={`${classes.sidePanel} ${isPanelOpen ? classes.sidePanelOpen : ""}`}
          >
            {isPanelOpen && activePlot && (
              <div className={classes.sidePanelInner}>
                <div className={classes.sidePanelHeader}>
                  <div className={classes.sidePanelTitleWrap}>
                    <Typography className={classes.sidePanelTitle}>
                      {activePlot.name}
                    </Typography>
                    <Typography className={classes.sidePanelSub}>
                      {activePlot.id} / {activePlot.municipality} / Lv{activePlot.lv}
                    </Typography>
                  </div>
                  <button
                    type="button"
                    className={classes.sidePanelClose}
                    onClick={() => setActivePlot(null)}
                    aria-label="閉じる"
                  >
                    <CloseIcon />
                  </button>
                </div>
                <Divider />
                <Box className={classes.thumbWrap}>
                  {activePlot.photos.map((photo) => (
                    <div
                      key={photo.id}
                      className={classes.thumbCard}
                      onClick={() => openViewer(photo.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          openViewer(photo.id);
                        }
                      }}
                    >
                      <div
                        className={classes.thumbPreview}
                        style={{ background: photo.thumb }}
                      >
                        <OpenInNewIcon />
                      </div>
                      <div className={classes.thumbMeta}>
                        <div>{photo.label}</div>
                        <div className={classes.thumbSub}>2026-05-28</div>
                      </div>
                    </div>
                  ))}
                </Box>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
};

Plot.propTypes = {
  setCurrentPage: PropTypes.func.isRequired,
  dateRange: PropTypes.array,
};

const mapStateToProps = (state) => ({
  dateRange: state.app.dateRange,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentPage: (param) => dispatch(setCurrentPage(param)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Plot);
